// The colour a picker should indicate is the one applied to the FIRST thing in the selection, which
// for a left-to-right selection is the leftmost one (CSPOL-2114).
//
// TipTap's `editor.getAttributes()` cannot answer that. For a node type it delegates to
// `getNodeAttributes`, which does `nodes.reverse().find(...)` and so returns the LAST cell in the
// selection; for a mark it delegates to `getMarkAttributes`, which returns the first run that
// carries the mark - skipping over leading text that has no colour at all and reporting a colour
// from further along. Both show the wrong swatch on a mixed selection, and they disagree with each
// other, so the three controls behaved differently from one another.

const markedColor = (marks, targets, attributeName) => {
  const mark = marks.find((candidate) => targets.includes(candidate.type.name));

  // An unset attribute on the textStyle mark reads as '' rather than null, and one mark carries
  // both the font colour and the highlight - so a run can hold the mark with this attribute unset.
  return (mark && mark.attrs[attributeName]) || null;
};

// `selection.from`/`to` are only `ranges[0]`, the "main range". A CellSelection has one range per
// cell and does not put them in document order, so those bounds describe a single arbitrary cell
// rather than the rectangle - walking them read the wrong cell's fill.
const selectionBounds = (selection) => ({
  from: Math.min(...selection.ranges.map((range) => range.$from.pos)),
  to: Math.max(...selection.ranges.map((range) => range.$to.pos))
});

/**
 * Reads `attributeName` off the first of `targets` in document order within the current selection.
 *
 * Targets are named the same way they are for `editor.getAttributes` - a mark name such as
 * `textStyle`, or node names such as `tableCell`/`tableHeader` - and are told apart by the schema,
 * so one walk covers both and a selection spanning a body cell and a header cell resolves to
 * whichever comes first rather than to whichever was listed first.
 *
 * Returns the value as authored, NOT canonicalised - the caller needs to tell "no colour" (null)
 * apart from "a colour that does not convert to hex", and CSS can render the latter even when the
 * palette cannot match it.
 */
const resolveSelectionColor = (editor, targets, attributeName) => {
  const state = editor?.state;
  const selection = state?.selection;

  // The toolbar renders before the editor has a view, so there is a tick where there is no
  // document to read. Answering "no colour" beats taking the whole toolbar down with it.
  if (!state?.doc || !selection?.ranges) {
    return null;
  }

  const markTargets = targets.filter((name) => Boolean(state.schema.marks[name]));
  const nodeTargets = targets.filter((name) => Boolean(state.schema.nodes[name]));

  // A collapsed cursor carries the marks the next keystroke would use, which is the colour the
  // picker should show - there is no text in the selection to read it from.
  if (selection.empty && markTargets.length) {
    return markedColor(state.storedMarks || selection.$head.marks(), markTargets, attributeName);
  }

  const { from, to } = selectionBounds(selection);
  let color = null;
  let resolved = false;

  state.doc.nodesBetween(from, to, (node) => {
    if (resolved) {
      return false;
    }

    if (nodeTargets.includes(node.type.name)) {
      color = node.attrs[attributeName] || null;
      resolved = true;

      return false;
    }

    // Only text carries a textStyle mark. Inline atoms such as images are stepped over rather than
    // answering "no colour" for a selection that starts on one.
    if (markTargets.length && node.isText) {
      color = markedColor(node.marks, markTargets, attributeName);
      resolved = true;

      return false;
    }

    return true;
  });

  return color;
};

export default resolveSelectionColor;
